import { useContext, useEffect, useState, useCallback } from 'react'
import { Heading } from '@/components/Heading'
import { Box } from '@/components/Box'
import Input from '@/components/Input'
import { Select } from '@/components/Select'
// @Adam - dummy select data for page layout will need selects updated to match the actual data
import { listOfCountries, loanTermOptions, occupationList } from '@/data/selectOptions'
import { Button } from '@/components/Button'
import { Question } from '@/components/Question'
import Textarea from '@/components/Textarea'
import { Checkbox } from '@/components/Checkbox'
import { ROUTES } from '@/config/routes'
import PurchaseItems from '@/pages/business/components/PurchaseItems'
import { Header } from '@/components/HeaderBusiness'
import { generateModelYearOptions, generateYearsOptions, generateMonthOptions } from '@/utils/dateUtils'
import { useFieldArray, useForm } from 'react-hook-form'
import useFormContext from '@/context/useFormContext'
import { emailRegex, defaultMinBusinessLoanAmount, mobileNumberRegex } from '@/utils/constant'
import AddressFinderInput from '@/components/AddressFinderInput'
import { ISO_3166_MAPPER } from '@/data/ISO_3166_MAPPER'
import { Address } from 'react-addressfinder'
import { DealerContext } from '@/context/DealerContext'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import debounce from 'lodash/debounce'
import { apiGet } from '@/services/apiService'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { preparePayload } from '@/services/saveService'
import { scrollToFirstError } from '@/utils/helper'
import { validateMoney } from '@/utils/validation'

const modelYearOptions = generateModelYearOptions(true, 1980)
const yearsInBusinessOptions = generateYearsOptions(true, 49)
const monthsOptions = generateMonthOptions(true)

interface CompanySearchResult {
  entityName: string
  nzbn: string
}

interface CompanyDetails {
  entityName: string
  nzbn: string
  sourceRegisterUniqueIdentifier: string
  registrationDate: string
  emailAddresses: Array<{
    uniqueIdentifier: string
    emailAddress: string
    emailPurpose: string | null
    emailPurposeDescription: string | null
    startDate: string
  }>
  phoneNumbers: Array<{
    uniqueIdentifier: string
    phonePurpose: string | null
    phonePurposeDescription: string | null
    phoneCountryCode: string
    phoneAreaCode: string
    phoneNumber: string
    startDate: string
  }>
  addresses: {
    addressList: Array<{
      uniqueIdentifier: string
      startDate: string
      endDate: string | null
      careOf: string
      address1: string
      address2: string
      address3: string
      address4: string | null
      postCode: string
      countryCode: string
      addressType: string
      pafId: string
    }>
  }
}

interface ApiResponse<T> {
  success: boolean
  status: string
  data: T
  responseMessage: string | null
  errors: string[]
}

const CompanySearchResults = ({ results, onSelect }: { results: CompanySearchResult[], onSelect: (result: CompanySearchResult) => void }) => {
  if (!results.length) return null

  return (
    <ul className="absolute max-h-60 overflow-auto rounded-md bg-white outline outline-1 outline-[var(--const-gray-light)] py-1 text-base shadow-lg z-50 w-full mt-1">
      {results.map((result, index) => (
        <li 
          key={index}
          className="group px-4 py-2 hover:bg-[var(--brand-1)] cursor-pointer border-b last:border-b-0 border-[var(--dropdown-divider)] last:border-none transition duration-300"
          onClick={() => onSelect(result)}
        >
          <div className="group-hover:text-[var(--const-white)] font-[var(--font-weight-bold)] transition duration-300">{result.entityName}</div>
          <div className="group-hover:text-[var(--const-white)] text-sm transition duration-300">{result.nzbn}</div>
        </li>
      ))}
    </ul>
  )
}

function AboutYourBusiness() {
  const [isAddAnotherItem, setIsAddAnotherItem] = useState(true)
  const { state, dispatch } = useFormContext()
  const dealerContext = useContext(DealerContext)
  const purchaseItemForm = useForm({
    defaultValues: {
      purchaseItems: state.business.aboutYourBusiness.purchaseItems
        ?.filter(item => item.itemType && item.itemType !== '')
        .map(item => ({
          ...item,
          isAdd: true
        })) || []
    }
  })
  const aboutYourBusinessForm = useForm({
    defaultValues: {
      businessType: state.business.aboutYourBusiness.businessType,
      preferredLoan: state.business.aboutYourBusiness.preferredLoan,
      businessDescription: state.business.aboutYourBusiness.businessDescription,
      tradingName: state.business.aboutYourBusiness?.soleTrader?.tradingName,
      occupation: state.business.aboutYourBusiness?.soleTrader?.occupation,
      timeInBusinessYearsST: state.business.aboutYourBusiness?.soleTrader?.timeInBusinessYears,
      timeInBusinessMonthsST: state.business.aboutYourBusiness?.soleTrader?.timeInBusinessMonths,
      haveNetProfitLastYearST: state.business.aboutYourBusiness?.soleTrader?.haveNetProfitLastYear,
      companyName: state.business.aboutYourBusiness?.limitedCompany?.companyName,
      companyNumber: state.business.aboutYourBusiness?.limitedCompany?.companyNumber,
      timeInBusinessYears: state.business.aboutYourBusiness?.limitedCompany?.timeInBusinessYears,
      timeInBusinessMonths: state.business.aboutYourBusiness?.limitedCompany?.timeInBusinessMonths,
      haveNetProfitLastYear: state.business.aboutYourBusiness?.limitedCompany?.haveNetProfitLastYear,
      phone: state.business.aboutYourBusiness?.limitedCompany?.phone,
      email: state.business.aboutYourBusiness?.limitedCompany?.email,
      physicalAddress: state.business.aboutYourBusiness?.limitedCompany?.physicalAddress,
      physicalCity: state.business.aboutYourBusiness?.limitedCompany?.physicalCity,
      physicalZIP: state.business.aboutYourBusiness?.limitedCompany?.physicalZIP,
      physicalCountry: state.business.aboutYourBusiness?.limitedCompany?.physicalCountry,
      postalAddress: state.business.aboutYourBusiness?.limitedCompany?.postalAddress,
      postalCity: state.business.aboutYourBusiness?.limitedCompany?.postalCity,
      postalZIP: state.business.aboutYourBusiness?.limitedCompany?.postalZIP,
      postalCountry: state.business.aboutYourBusiness?.limitedCompany?.postalCountry,
      registeredAddress: state.business.aboutYourBusiness?.limitedCompany?.registeredAddress,
      registeredCity: state.business.aboutYourBusiness?.limitedCompany?.registeredCity,
      registeredZIP: state.business.aboutYourBusiness?.limitedCompany?.registeredZIP,
      registeredCountry: state.business.aboutYourBusiness?.limitedCompany?.registeredCountry,
      reuseForPostalAddress: state.business.aboutYourBusiness?.limitedCompany?.reuseForPostalAddress,
      reuseForRegisteredAddress: state.business.aboutYourBusiness?.limitedCompany?.reuseForRegisteredAddress,
      limitedCompany: state.business.aboutYourBusiness?.limitedCompany,
      soleTrader: state.business.aboutYourBusiness?.soleTrader
    }
  })

  // Add this effect to update form values when context changes (e.g., after API loads)
  useEffect(() => {
    // Support legacy values by transforming 'Sole Trader' to 'SOLO' and 'Limited Company' to 'LIMITED'
    const businessType = state.business.aboutYourBusiness.businessType === 'Sole Trader' ? 'SOLE' :
      state.business.aboutYourBusiness.businessType === 'Limited Company' ? 'LIMITED' :
      state.business.aboutYourBusiness.businessType

    aboutYourBusinessForm.reset({
      businessType: businessType,
      preferredLoan: state.business.aboutYourBusiness.preferredLoan,
      businessDescription: state.business.aboutYourBusiness.businessDescription,
      tradingName: state.business.aboutYourBusiness?.soleTrader?.tradingName,
      occupation: state.business.aboutYourBusiness?.soleTrader?.occupation,
      timeInBusinessYearsST: state.business.aboutYourBusiness?.soleTrader?.timeInBusinessYears,
      timeInBusinessMonthsST: state.business.aboutYourBusiness?.soleTrader?.timeInBusinessMonths,
      haveNetProfitLastYearST: state.business.aboutYourBusiness?.soleTrader?.haveNetProfitLastYear,
      companyName: state.business.aboutYourBusiness?.limitedCompany?.companyName,
      companyNumber: state.business.aboutYourBusiness?.limitedCompany?.companyNumber,
      timeInBusinessYears: state.business.aboutYourBusiness?.limitedCompany?.timeInBusinessYears,
      timeInBusinessMonths: state.business.aboutYourBusiness?.limitedCompany?.timeInBusinessMonths,
      haveNetProfitLastYear: state.business.aboutYourBusiness?.limitedCompany?.haveNetProfitLastYear,
      phone: state.business.aboutYourBusiness?.limitedCompany?.phone,
      email: state.business.aboutYourBusiness?.limitedCompany?.email,
      physicalAddress: state.business.aboutYourBusiness?.limitedCompany?.physicalAddress,
      physicalCity: state.business.aboutYourBusiness?.limitedCompany?.physicalCity,
      physicalZIP: state.business.aboutYourBusiness?.limitedCompany?.physicalZIP,
      physicalCountry: state.business.aboutYourBusiness?.limitedCompany?.physicalCountry,
      postalAddress: state.business.aboutYourBusiness?.limitedCompany?.postalAddress,
      postalCity: state.business.aboutYourBusiness?.limitedCompany?.postalCity,
      postalZIP: state.business.aboutYourBusiness?.limitedCompany?.postalZIP,
      postalCountry: state.business.aboutYourBusiness?.limitedCompany?.postalCountry,
      registeredAddress: state.business.aboutYourBusiness?.limitedCompany?.registeredAddress,
      registeredCity: state.business.aboutYourBusiness?.limitedCompany?.registeredCity,
      registeredZIP: state.business.aboutYourBusiness?.limitedCompany?.registeredZIP,
      registeredCountry: state.business.aboutYourBusiness?.limitedCompany?.registeredCountry,
      reuseForPostalAddress: state.business.aboutYourBusiness?.limitedCompany?.reuseForPostalAddress,
      reuseForRegisteredAddress: state.business.aboutYourBusiness?.limitedCompany?.reuseForRegisteredAddress,
      limitedCompany: state.business.aboutYourBusiness?.limitedCompany,
      soleTrader: state.business.aboutYourBusiness?.soleTrader
    })
  }, [state.business.aboutYourBusiness, aboutYourBusinessForm.reset])

  useEffect(() => {
    purchaseItemForm.reset({
      purchaseItems: state.business.aboutYourBusiness.purchaseItems?.map(item => ({
        ...item,
        isAdd: item.itemType !== ''
      })) || []
    })
  }, [state.business.aboutYourBusiness.purchaseItems, purchaseItemForm.reset])

  const [updatedPhysicalAddress, setUpdatedPhysicalAddress] = useState(state.business.aboutYourBusiness?.limitedCompany?.physicalAddress)
  const [updatedPostalAddress, setUpdatedPostalAddress] = useState(state.business.aboutYourBusiness?.limitedCompany?.postalAddress)
  const [updatedRegisteredAddress, setUpdatedRegisteredAddress] = useState(state.business.aboutYourBusiness?.limitedCompany?.registeredAddress)

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => {
    aboutYourBusinessForm.handleSubmit(() => {})()
    purchaseItemForm.handleSubmit(() => {})()
  }

  // Register the validation trigger when the component mounts
  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  useEffect(() => {
    // We need to attach required dealer details to the form context
    if (dealerContext?.dealerInformation.dealerId || dealerContext?.dealerInformation.dealerUniqueIdentifier) {
      dispatch({
        type: 'UPDATE_DEALER_DETAILS',
        payload: {
          dealerId: dealerContext?.dealerInformation.dealerId ?? null,
          dealerUniqueIdentifier: dealerContext?.dealerInformation.dealerUniqueIdentifier ?? null
        }
      })
    }
  }, [dealerContext?.dealerInformation.dealerId, dealerContext?.dealerInformation.dealerUniqueIdentifier, dispatch])

  const isSoleTrader = aboutYourBusinessForm.watch('businessType') === 'SOLE'
  const isLimitedCompany = aboutYourBusinessForm.watch('businessType') === 'LIMITED'
  const showYourPostalAddress = !aboutYourBusinessForm.watch('reuseForPostalAddress')
  const showYourRegisteredAddress = !aboutYourBusinessForm.watch('reuseForRegisteredAddress')
  const businessType = aboutYourBusinessForm.watch('businessType')

  const { fields: purchaseItemsFields, append, remove, update } = useFieldArray({
    control: purchaseItemForm.control,
    name: 'purchaseItems'
  })

  const excludedPurchaseItems = ['MV', 'RM', 'TB', 'MH']
  const firstOption = dealerContext?.dealerInformation?.buyingOptions?.find(option => !excludedPurchaseItems.includes(option)) || '440'
  const handleSelectPurchaseItem = () => {
    append({
      itemType: firstOption,
      year: '',
      purchasePrice: '',
      cashDeposit: null,
      make: '',
      model: '',
      isAdd: false
    })
    purchaseItemForm.register(`purchaseItems.${purchaseItemsFields.length}.year` as const, {
      required: 'Model year is required'
    })
    purchaseItemForm.register(`purchaseItems.${purchaseItemsFields.length}.make` as const)
    purchaseItemForm.register(`purchaseItems.${purchaseItemsFields.length}.model` as const)

    setIsAddAnotherItem(false)
  }

  const handleRemovePurchaseItem = (index: number) => {
    remove(index)
  }

  if (purchaseItemsFields.length === 0) {
    handleSelectPurchaseItem()
  }

  // Handle our custom input components for about your business
  const handleCustomInputChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    aboutYourBusinessForm.setValue(fieldName, value)

    if (['physicalAddress', 'postalAddress', 'registeredAddress'].includes(fieldName)) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.trigger(fieldName)
    }

    if (aboutYourBusinessForm.formState.isSubmitted) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.trigger(fieldName)
    }
  }

  const updateAddressFields = (prefix: string, address: Address) => {
    const cityField = prefix ? `${prefix}City` : 'city'
    const zipField = prefix ? `${prefix}ZIP` : 'ZIP'
    const countryField = prefix ? `${prefix}Country` : 'country'

    if (address.city) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.setValue(cityField, address.city); aboutYourBusinessForm.clearErrors(cityField)
    } else {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.setValue(cityField, ''); aboutYourBusinessForm.clearErrors(cityField)
    }
    if (address.postcode) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.setValue(zipField, address.postcode); aboutYourBusinessForm.clearErrors(zipField)
    } else {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.setValue(zipField, ''); aboutYourBusinessForm.clearErrors(zipField)
    }
    if (address.country) {
      const countryISO3 = Object.keys(ISO_3166_MAPPER).find(key => ISO_3166_MAPPER[key] === address.country)

      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.setValue(countryField, countryISO3); aboutYourBusinessForm.clearErrors(countryField)
    } else {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      aboutYourBusinessForm.setValue(countryField, ''); aboutYourBusinessForm.clearErrors(countryField)
    }
  }

  const handleCustomAddressFinderInputChange = (fieldName: string, value: string, address: Address) => {
    if (address) {
      if (fieldName === 'physicalAddress') {
        updateAddressFields('physical', address)
      } else if (fieldName === 'registeredAddress') {
        updateAddressFields('registered', address)
      } else if (fieldName === 'postalAddress') {
        updateAddressFields('postal', address)
      }
    }

    handleCustomInputChange(fieldName, value)
  }

  const onPurchaseItemSubmit = (data: any) => {
    const purcheseItemData = data.purchaseItems[data.purchaseItems.length - 1]
    
    // Update the last purchase item to show the add another item button
    update(data.purchaseItems.length - 1, {
      ...purcheseItemData,
      isAdd: true,
      itemType: purcheseItemData.itemType || firstOption
    })

    setIsAddAnotherItem(true)

    // Ensure cashDeposit is always null if it's an empty string
    const updatedPurchaseItems = data.purchaseItems.map((item: any) => ({
      ...item,
      cashDeposit: item.cashDeposit === '' ? null : item.cashDeposit,
      itemType: item.itemType || firstOption
    }))

    // Get latest values from the main form
    const aboutFormValues = aboutYourBusinessForm.getValues()

    // Build the payload from the latest form values, reconstructing nested objects
    dispatch({
      type: 'UPDATE_BUSINESS',
      step: 'aboutYourBusiness',
      payload: {
        ...aboutFormValues,
        purchaseItems: updatedPurchaseItems,
        soleTrader: {
          ...state.business.aboutYourBusiness.soleTrader,
          tradingName: aboutFormValues.tradingName,
          occupation: aboutFormValues.occupation,
          timeInBusinessYears: aboutFormValues.timeInBusinessYearsST,
          timeInBusinessMonths: aboutFormValues.timeInBusinessMonthsST,
          haveNetProfitLastYear: aboutFormValues.haveNetProfitLastYearST,
        },
        limitedCompany: {
          ...state.business.aboutYourBusiness.limitedCompany,
          companyName: aboutFormValues.companyName,
          companyNumber: aboutFormValues.companyNumber,
          timeInBusinessYears: aboutFormValues.timeInBusinessYears,
          timeInBusinessMonths: aboutFormValues.timeInBusinessMonths,
          haveNetProfitLastYear: aboutFormValues.haveNetProfitLastYear,
          phone: aboutFormValues.phone,
          email: aboutFormValues.email,
          physicalAddress: aboutFormValues.physicalAddress,
          physicalCity: aboutFormValues.physicalCity,
          physicalZIP: aboutFormValues.physicalZIP,
          physicalCountry: aboutFormValues.physicalCountry,
          postalAddress: aboutFormValues.postalAddress,
          postalCity: aboutFormValues.postalCity,
          postalZIP: aboutFormValues.postalZIP,
          postalCountry: aboutFormValues.postalCountry,
          registeredAddress: aboutFormValues.registeredAddress,
          registeredCity: aboutFormValues.registeredCity,
          registeredZIP: aboutFormValues.registeredZIP,
          registeredCountry: aboutFormValues.registeredCountry,
          reuseForPostalAddress: aboutFormValues.reuseForPostalAddress,
          reuseForRegisteredAddress: aboutFormValues.reuseForRegisteredAddress,
        }
      }
    })
  }

  const onAboutYourBusinessSubmit = async (data: any) => {
    // First validate the purchase item form
    const isPurchaseItemValid = await purchaseItemForm.trigger()
    if (!isPurchaseItemValid) {
      // If purchase item form is not valid, scroll to the top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    // Since we have separate forms, we need to get the values from the purchase item form in case the user modified any of the fields and update the state
    const purchaseItemsValues = purchaseItemForm.getValues('purchaseItems').map((item: any) => ({
      ...item,
      cashDeposit: item.cashDeposit === '' ? null : item.cashDeposit
    }))

    const anyEmptyItemType = purchaseItemsValues.some((item: any) => !item.itemType)
    if (anyEmptyItemType) {
      window.alert('Please select the purchase item by clicking on the "Continue" button')
      // If any item type is empty, scroll to the top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    data = {
      ...state,
      personal: null,
      business: {
        ...state.business,
        aboutYourBusiness: {
          ...state.business.aboutYourBusiness,
          purchaseItems: purchaseItemsValues,
          businessType: data.businessType,
          preferredLoan: data.preferredLoan,
          businessDescription: data.businessDescription,
          soleTrader: {
            ...state.business.aboutYourBusiness.soleTrader,
            tradingName: data.tradingName,
            occupation: data.occupation,
            timeInBusinessYears: data.timeInBusinessYearsST,
            timeInBusinessMonths: data.timeInBusinessMonthsST,
            haveNetProfitLastYear: data.haveNetProfitLastYearST
          },
          limitedCompany: {
            ...state.business.aboutYourBusiness.limitedCompany,
            companyName: data.companyName,
            companyNumber: data.companyNumber,
            timeInBusinessYears: data.timeInBusinessYears,
            timeInBusinessMonths: data.timeInBusinessMonths,
            haveNetProfitLastYear: data.haveNetProfitLastYear,
            phone: data.phone,
            email: data.email,
            physicalAddress: data.physicalAddress,
            physicalCity: data.physicalCity,
            physicalZIP: data.physicalZIP,
            physicalCountry: data.physicalCountry,
            postalAddress: showYourPostalAddress ? data.postalAddress : '',
            postalCity: showYourPostalAddress ? data.postalCity : '',
            postalZIP: showYourPostalAddress ? data.postalZIP : '',
            postalCountry: showYourPostalAddress ? data.postalCountry : '',
            registeredAddress: showYourRegisteredAddress ? data.registeredAddress : '',
            registeredCity: showYourRegisteredAddress ? data.registeredCity : '',
            registeredZIP: showYourRegisteredAddress ? data.registeredZIP : '',
            registeredCountry: showYourRegisteredAddress ? data.registeredCountry : '',
            reuseForPostalAddress: data.reuseForPostalAddress,
            reuseForRegisteredAddress: data.reuseForRegisteredAddress
          }
        }
      }
    }

    dispatchFormData(data)

    // Depending on the step, we want to send the payload to the API expecting to pass only the current step data
    // and all other steps as null, we first want to deepClone the state, then set other loan steps to null
    const payload = preparePayload(data, 'business', 'aboutYourBusiness')

    // If it's a sole trader set limitedCompany to null and vice versa
    if (isSoleTrader) {
      payload.business.aboutYourBusiness.limitedCompany = null
    } else {
      payload.business.aboutYourBusiness.soleTrader = null
    }

    handleFormSave(payload, ROUTES.BUSINESS.ABOUT_YOU.path)
  }

  // Register all custom input components for purchase item form
  useEffect(() => {
    purchaseItemForm.register('purchaseItems', { required: 'At least one purchase item is required' })
  }, [])

  // Register all custom input components for about your business form
  useEffect(() => {
    aboutYourBusinessForm.register('businessType', { required: 'Business type is required' })
    aboutYourBusinessForm.register('preferredLoan', { required: 'Preferred loan is required' })
    aboutYourBusinessForm.register('businessDescription', { 
      required: 'Business description is required',
      maxLength: {
        value: 200,
        message: 'Business description must not exceed 200 characters'
      }
    })
  }, [])

  // Business type-specific fields
  useEffect(() => {
    aboutYourBusinessForm.clearErrors()

    if (businessType === 'SOLE') {
      aboutYourBusinessForm.register('occupation', { required: 'Occupation is required' })
      aboutYourBusinessForm.register('timeInBusinessYearsST', { required: 'Time in business years is required' })
      aboutYourBusinessForm.register('timeInBusinessMonthsST', { required: 'Time in business months is required' })
      aboutYourBusinessForm.register('haveNetProfitLastYearST', {
        validate: (value) => value === true || value === false || 'Have net profit last year is required'
      })
      aboutYourBusinessForm.register('tradingName', { required: 'Trading name is required' })

      aboutYourBusinessForm.unregister('companyName')
      aboutYourBusinessForm.unregister('companyNumber')
      aboutYourBusinessForm.unregister('timeInBusinessYears')
      aboutYourBusinessForm.unregister('timeInBusinessMonths')
      aboutYourBusinessForm.unregister('postalAddress')
      aboutYourBusinessForm.unregister('postalCity')
      aboutYourBusinessForm.unregister('postalZIP')
      aboutYourBusinessForm.unregister('postalCountry')
      aboutYourBusinessForm.unregister('registeredAddress')
      aboutYourBusinessForm.unregister('registeredCity')
      aboutYourBusinessForm.unregister('registeredZIP')
      aboutYourBusinessForm.unregister('registeredCountry')
      aboutYourBusinessForm.unregister('phone')
      aboutYourBusinessForm.unregister('email')
      aboutYourBusinessForm.unregister('physicalAddress')
      aboutYourBusinessForm.unregister('physicalCity')
      aboutYourBusinessForm.unregister('physicalZIP')
      aboutYourBusinessForm.unregister('physicalCountry')
    }

    if (businessType === 'LIMITED') {
      aboutYourBusinessForm.register('companyName', { required: 'Company name is required' })
      aboutYourBusinessForm.register('companyNumber', { required: 'Company number is required' })
      aboutYourBusinessForm.register('timeInBusinessYears', { required: 'Time in business years is required' })
      aboutYourBusinessForm.register('timeInBusinessMonths', { required: 'Time in business months is required' })
      aboutYourBusinessForm.register('haveNetProfitLastYear', {
        validate: (value) => value === true || value === false || 'Have net profit last year is required'
      })
      aboutYourBusinessForm.register('physicalCountry', { required: 'Physical country is required' })
      aboutYourBusinessForm.register('physicalAddress', { required: 'Address is required' })

      // Unregister sole trader fields
      aboutYourBusinessForm.unregister('occupation')
      aboutYourBusinessForm.unregister('timeInBusinessYearsST')
      aboutYourBusinessForm.unregister('timeInBusinessMonthsST')
    }
  }, [businessType])

  // Address field registration (separate effect for better control)
  useEffect(() => {
    if (businessType === 'LIMITED') {
      if (showYourPostalAddress) {
        aboutYourBusinessForm.register('postalAddress', { required: 'Address is required' })
        aboutYourBusinessForm.register('postalCountry', { required: 'Postal country is required' })
      } else {
        aboutYourBusinessForm.unregister('postalAddress')
        aboutYourBusinessForm.unregister('postalCountry')
      }

      if (showYourRegisteredAddress) {
        aboutYourBusinessForm.register('registeredAddress', { required: 'Address is required' })
        aboutYourBusinessForm.register('registeredCountry', { required: 'Registered country is required' })
      } else {
        aboutYourBusinessForm.unregister('registeredAddress')
        aboutYourBusinessForm.unregister('registeredCountry')
      }
    }
  }, [businessType, showYourPostalAddress, showYourRegisteredAddress])

  const { handleFormSave } = useFormSubmission({
    onSuccess: (response) => {
      console.log('Form submission successful:', response)

      // Update our context with the response.data.formId
      dispatch({
        type: 'UPDATE_FORM_METADATA',
        payload: {
          formId: response.data.formId,
          dealerId: response.data.dealerId
        }
      })
    },
    onError: (error) => {
      console.error('Error during form save:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_BUSINESS',
      step: 'aboutYourBusiness',
      payload: data.business.aboutYourBusiness
    })
  }

  const [companySearchResults, setCompanySearchResults] = useState<CompanySearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Real API call to search companies
  const searchCompanies = async (query: string): Promise<CompanySearchResult[]> => {
    try {
      const result: ApiResponse<CompanySearchResult[]> | null = await apiGet(`BusinessGovt/SearchBusiness?company=${encodeURIComponent(query)}`)
      return result?.data ?? []
    } catch (error) {
      return []
    }
  }

  // Get company details by NZBN
  const getCompanyDetails = async (nzbn: string): Promise<CompanyDetails | null> => {
    try {
      const result: ApiResponse<CompanyDetails> = await apiGet(`BusinessGovt/GetCompanyDetailsByNzbn/${nzbn}`)
      return result.data
    } catch (error) {
      return null
    }
  }

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setCompanySearchResults([])
        return
      }

      setIsSearching(true)
      try {
        const results = await searchCompanies(query)
        setCompanySearchResults(results)
      } catch (error) {
        setCompanySearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300),
    []
  )

  // Handle company selection
  const handleCompanySelect = async (result: CompanySearchResult) => {
    aboutYourBusinessForm.setValue('companyName', result.entityName)
    setCompanySearchResults([])
    
    // Fetch additional company details
    const details = await getCompanyDetails(result.nzbn)
    if (details) {
      // When switching to the FIS system the company number will be passed as nzbn
      aboutYourBusinessForm.setValue('companyNumber', details.sourceRegisterUniqueIdentifier)

      // Handle addresses
      if (details.addresses?.addressList?.length > 0) {
        // Get physical address (SERVICE or OFFICE)
        let physicalAddressObj = details.addresses.addressList.find(item => item.addressType === 'SERVICE')
        if (!physicalAddressObj) physicalAddressObj = details.addresses.addressList.find(item => item.addressType === 'OFFICE')
        if (physicalAddressObj) {
          // Format physical address
          const physicalAddress = [
            physicalAddressObj.address1,
            physicalAddressObj.address2,
            physicalAddressObj.address3,
            physicalAddressObj.address4
          ].filter(Boolean).join(', ')

          // Reuse the existing custom address finder on input change function
          const physicalAddressObject = {
            line1: '', line2: '', suburb: '', state: '',
            postcode: physicalAddressObj.postCode,
            country: physicalAddressObj.countryCode,
            city: physicalAddressObj.address3
          }

          handleCustomAddressFinderInputChange('physicalAddress', physicalAddress, physicalAddressObject)
          setUpdatedPhysicalAddress(physicalAddress)

          // Get postal address
          const postalAddressObj = details.addresses.addressList.find(item => item.addressType === 'POSTAL')
          if (postalAddressObj) {
            // Format postal address
            const postalAddress = [
              postalAddressObj.address1,
              postalAddressObj.address2,
              postalAddressObj.address3,
              postalAddressObj.address4
            ].filter(Boolean).join(', ')

            const postalAddressObject = {
              line1: '', line2: '', suburb: '', state: '',
              postcode: postalAddressObj.postCode,
              country: postalAddressObj.countryCode,
              city: postalAddressObj.address3
            }

            // Small delay workaround to bypass the react state update issue
            window.setTimeout(() => {
              handleCustomAddressFinderInputChange('postalAddress', postalAddress, postalAddressObject)
              setUpdatedPostalAddress(postalAddress)
            }, 250)
          } else {
            // Clear postal address fields if no postal address found
            aboutYourBusinessForm.setValue('postalAddress', '')
            aboutYourBusinessForm.setValue('postalCity', '')
            aboutYourBusinessForm.setValue('postalZIP', '')
            aboutYourBusinessForm.setValue('postalCountry', '')
          }

          // Get registered address
          const registeredAddressObj = details.addresses.addressList.find(item => item.addressType === 'REGISTERED')
          if (registeredAddressObj) {
            // Format registered address
            const registeredAddress = [
              registeredAddressObj.address1,
              registeredAddressObj.address2,
              registeredAddressObj.address3,
              registeredAddressObj.address4
            ].filter(Boolean).join(', ')

            const registeredAddressObject = {
              line1: '', line2: '', suburb: '', state: '',
              postcode: registeredAddressObj.postCode,
              country: registeredAddressObj.countryCode,
              city: registeredAddressObj.address3
            }

            window.setTimeout(() => {
              handleCustomAddressFinderInputChange('registeredAddress', registeredAddress, registeredAddressObject)
              setUpdatedRegisteredAddress(registeredAddress)
            }, 500)
          }
        } else {
          // Clear registered address fields if no registered address found
          const emptyAddressObject = {
            line1: '', line2: '', suburb: '', state: '',
            postcode: '',
            country: '',
            city: ''
          }

          handleCustomAddressFinderInputChange('registeredAddress', '', emptyAddressObject)
          window.setTimeout(() => handleCustomAddressFinderInputChange('postalAddress', '', emptyAddressObject), 250)
          window.setTimeout(() => handleCustomAddressFinderInputChange('physicalAddress', '', emptyAddressObject), 500)

          const registeredAddressElement = document.getElementById('registeredAddress') as HTMLInputElement
          const postalAddressElement = document.getElementById('postalAddress') as HTMLInputElement
          const physicalAddressElement = document.getElementById('physicalAddress') as HTMLInputElement

          if (registeredAddressElement) registeredAddressElement.value = ''
          if (postalAddressElement) postalAddressElement.value = ''
          if (physicalAddressElement) physicalAddressElement.value = ''
        }
      }
    }
  }

  return (
    <>
      <Header />
      <Heading>What are you looking to purchase?</Heading>
      {state.isPostCall && (
        <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto">We've included some information from your most recent loan in the form below. You just need to review this information and update anything if necessary.</p>
      )}
      <form className='formBox' onSubmit={purchaseItemForm.handleSubmit(onPurchaseItemSubmit)}>
        {purchaseItemsFields.map((field, index) => (
          <Box key={field.id}>
            <PurchaseItems
              onSelect={(item) => purchaseItemForm.setValue(`purchaseItems.${index}.itemType`, item)}
              defaultValue={field.itemType}
              completedSelectingPurchaseItem={field.isAdd}
              buyingOptions={{
                icons: dealerContext?.dealerThemeIcon?.buyingOptions || [],
                list: dealerContext?.dealerInformation?.buyingOptions || []
              }}
            />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select
                label='Model year'
                required={true}
                selectOptions={modelYearOptions}
                defaultValue={field.year}
                error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.year}
                onChange={(value) => {
                  purchaseItemForm.setValue(`purchaseItems.${index}.year`, value)
                  purchaseItemForm.clearErrors(`purchaseItems.${index}.year`)
                }}
              />
              <Input
                label='Cash price of asset (Including GST)'
                inputId={`purchaseItems[${index}]purchasePrice`}
                required={true}
                money={true}
                placeholder='Amount'
                defaultValue={field.purchasePrice}
                {...purchaseItemForm.register(`purchaseItems.${index}.purchasePrice` as const, {
                  validate: (value) => {
                    const loanAmount = dealerContext?.dealerInformation.minimumLoanAmount || defaultMinBusinessLoanAmount
                    const result = validateMoney(true, value, loanAmount)
                    if (result !== true) return result
                    // preserve any other custom logic here if needed
                    return true
                  },
                  onChange: (e) => {
                    const value = e.target.value
                    purchaseItemForm.setValue(`purchaseItems.${index}.purchasePrice`, value)
                    purchaseItemForm.clearErrors(`purchaseItems.${index}.purchasePrice`)
                  }
                })}
                error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.purchasePrice}
              />
              <Input
                label='Deposit'
                inputId='deposit'
                money={true}
                placeholder='Deposit amount'
                defaultValue={field.cashDeposit || ''}
                {...purchaseItemForm.register(`purchaseItems.${index}.cashDeposit` as const, {
                  validate: (value) => validateMoney(false, value),
                  onChange: (e) => {
                    let value = e.target.value
                    if (value === '') value = null
                    purchaseItemForm.setValue(`purchaseItems.${index}.cashDeposit`, value)
                    purchaseItemForm.clearErrors(`purchaseItems.${index}.cashDeposit`)
                  }
                })}
                error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.cashDeposit}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              {(dealerContext?.dealerInformation.model.length || 0) <= 1 ?
                <Input
                  label='Make (if known)'
                  inputId='make'
                  placeholder='Enter make'
                  defaultValue={field.make}
                  {...purchaseItemForm.register(`purchaseItems.${index}.make` as const, {
                    onChange: (e) => {
                      const value = e.target.value
                      purchaseItemForm.setValue(`purchaseItems.${index}.make`, value)
                      purchaseItemForm.clearErrors(`purchaseItems.${index}.make`)
                    }
                  })}
                  error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.make}
                />
                :
                <Select
                  label='Make (if known)'
                  selectOptions={dealerContext?.dealerInformation.make || []}
                  defaultValue={field.make}
                  error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.make}
                  onChange={(value) => {
                    purchaseItemForm.setValue(`purchaseItems.${index}.make`, value)
                    purchaseItemForm.clearErrors(`purchaseItems.${index}.make`)
                  }}
                />
              }
              {(dealerContext?.dealerInformation.model.length || 0) <= 1 ?
                <Input
                  label='Model (if known)'
                  inputId='model'
                  placeholder='Enter model'
                  defaultValue={field.model}
                  {...purchaseItemForm.register(`purchaseItems.${index}.model` as const, {
                    onChange: (e) => {
                      const value = e.target.value
                      purchaseItemForm.setValue(`purchaseItems.${index}.model`, value)
                      purchaseItemForm.clearErrors(`purchaseItems.${index}.model`)
                    }
                  })}
                  error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.model}
                /> :
                <Select
                  label='Model (if known)'
                  selectOptions={dealerContext?.dealerInformation.model || []}
                  defaultValue={field.model}
                  error={purchaseItemForm.formState.errors?.purchaseItems?.[index]?.model}
                  onChange={(value) => {
                    purchaseItemForm.setValue(`purchaseItems.${index}.model`, value)
                    purchaseItemForm.clearErrors(`purchaseItems.${index}.model`)
                  }}
                />
              }
              <div>
                <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-medium max-md:hidden'>&nbsp;</span>
                <div className='relative mt-2.5 flex justify-end'>
                  {!field.isAdd &&
                    <Button type='submit' >Continue</Button>
                  }

                  {field.isAdd &&
                    <Button icon='remove' style='text' onClick={() => handleRemovePurchaseItem(index)}>Remove</Button>
                  }
                </div>
              </div>
            </div>
          </Box>
        ))}
        {isAddAnotherItem &&
          <div className='flex justify-center pt-8 pb-12'>
            <Button icon='add' onClick={() => handleSelectPurchaseItem()}>Add another item</Button>
          </div>
        }
      </form>

      <form 
        className='formBox' 
        onSubmit={async (e) => {
          aboutYourBusinessForm.handleSubmit(onAboutYourBusinessSubmit)(e)

          window.setTimeout(async () => {
            const isPurchaseItemValid = await purchaseItemForm.trigger()
            if (!isPurchaseItemValid) {
              // If purchase item form is not valid, scroll to the top of the page
              window.scrollTo({ top: 0, behavior: 'smooth' })
            } else {
              if (Object.keys(aboutYourBusinessForm.formState.errors).length > 0) {
                scrollToFirstError()
              }
            }
          }, 0)
      }}>
        <Box>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            <div />
            <Select
              label='What is your preferred loan term?'
              required={true}
              selectOptions={loanTermOptions}
              defaultValue={state.business.aboutYourBusiness.preferredLoan}
              error={aboutYourBusinessForm.formState.errors?.preferredLoan}
              onChange={value => handleCustomInputChange('preferredLoan', value)}
            />
          </div>
          <div className='pt-4'>
            <Question
              title='What is your type of business? *'
              name='businessType'
              defaultValue={state.business.aboutYourBusiness.businessType === 'Sole Trader' ? 'SOLE' :
                state.business.aboutYourBusiness.businessType === 'Limited Company' ? 'LIMITED' :
                state.business.aboutYourBusiness.businessType}
              onChange={value => handleCustomInputChange('businessType', value)}
              error={aboutYourBusinessForm.formState.errors.businessType}
              options={[
                { id: 'businessTypeSoleTrader', title: 'Sole trader', value: 'SOLE' },
                { id: 'businessTypeLimitedCompany', title: 'Limited company', value: 'LIMITED' }
              ]}
            />
          </div>
        </Box>
        {isSoleTrader &&
          <Box>
            <Textarea
              label='Give us a brief description of what your business does'
              id='businessDescription'
              required={true}
              placeholder='Tell us more'
              maxLength={200}
              showCharacterCount={true}
              defaultValue={state.business.aboutYourBusiness?.businessDescription}
              error={aboutYourBusinessForm.formState.errors.businessDescription}
              {...aboutYourBusinessForm.register('businessDescription', { 
                required: 'Business description is required',
                maxLength: {
                  value: 200,
                  message: 'Business description must not exceed 200 characters'
                }
              })}
            />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Input
                label='Trading name'
                inputId='tradingName'
                required={true}
                placeholder='Trading name'
                defaultValue={state.business.aboutYourBusiness?.soleTrader?.tradingName}
                error={aboutYourBusinessForm.formState.errors?.tradingName}
                {...aboutYourBusinessForm.register('tradingName', { required: 'Trading name is required' })}
              />
              <Select
                label='Occupation'
                required={true}
                selectOptions={occupationList}
                defaultValue={state.business.aboutYourBusiness?.soleTrader?.occupation}
                error={aboutYourBusinessForm.formState.errors?.occupation}
                onChange={value => handleCustomInputChange('occupation', value)}
              />
              <Question
                title='Did you make a net profit past year? *'
                name='haveNetProfitLastYearSoleTrader'
                style='field'
                options={[
                  { id: 'profitPastYearYes', title: 'Yes', value: true },
                  { id: 'profitPastYearNo', title: 'No', value: false }
                ]}
                defaultValue={state.business.aboutYourBusiness?.soleTrader?.haveNetProfitLastYear ? true : false}
                error={aboutYourBusinessForm.formState.errors?.haveNetProfitLastYearST}
                onChange={value => handleCustomInputChange('haveNetProfitLastYearST', value)}
              />
            </div>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
              <Select
                label='Time in business'
                required={true}
                selectOptions={yearsInBusinessOptions}
                error={aboutYourBusinessForm.formState.errors?.timeInBusinessYearsST}
                defaultValue={state.business.aboutYourBusiness?.soleTrader?.timeInBusinessYears}
                onChange={value => handleCustomInputChange('timeInBusinessYearsST', value)}
              />
              <Select
                label='Months'
                hideLabel={true}
                required={true}
                selectOptions={monthsOptions}
                error={aboutYourBusinessForm.formState.errors?.timeInBusinessMonthsST}
                defaultValue={state.business.aboutYourBusiness?.soleTrader?.timeInBusinessMonths}
                onChange={value => handleCustomInputChange('timeInBusinessMonthsST', value)}
              />
            </div>
          </Box>
        }

        {isLimitedCompany &&
          <>
            <Box>
              <Textarea
                label='Give us a brief description of what your business does'
                id='businessDescription'
                required={true}
                placeholder='Tell us more'
                maxLength={200}
                showCharacterCount={true}
                defaultValue={state.business.aboutYourBusiness?.businessDescription}
                error={aboutYourBusinessForm.formState.errors.businessDescription}
                {...aboutYourBusinessForm.register('businessDescription', { 
                  required: 'Business description is required',
                  maxLength: {
                    value: 200,
                    message: 'Business description must not exceed 200 characters'
                  }
                })}
              />
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <div className='relative'>
                  <Input
                    label='Company name'
                    inputId='companyName'
                    placeholder='Company name'
                    required={true}
                    companySearch={true}
                    defaultValue={state.business.aboutYourBusiness?.limitedCompany?.companyName}
                    error={aboutYourBusinessForm.formState.errors?.companyName}
                    {...aboutYourBusinessForm.register('companyName', { 
                      required: 'Company name is required',
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value
                        aboutYourBusinessForm.setValue('companyName', value)
                        aboutYourBusinessForm.clearErrors('companyName')
                        debouncedSearch(value)
                      }
                    })}
                  />
                  {isSearching && <ArrowPathIcon className='absolute size-6 animate-spin top-[47px] right-3' aria-hidden='true' />}
                  <CompanySearchResults 
                    results={companySearchResults} 
                    onSelect={handleCompanySelect}
                  />
                </div>
                <Input
                  label='Company number'
                  inputId='companyNumber'
                  placeholder='Company number'
                  required={true}
                  defaultValue={state.business.aboutYourBusiness?.limitedCompany?.companyNumber}
                  error={aboutYourBusinessForm.formState.errors?.companyNumber}
                  {...aboutYourBusinessForm.register('companyNumber', { required: 'Company number is required' })}
                />
              </div>
              <div className='flex flex-col gap-8'>
                <div className='flex flex-col md:grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                  <div className='grid grid-cols-2 gap-7 md:gap-8'>
                    <Select
                      label='Time in business'
                      required={true}
                      selectOptions={yearsInBusinessOptions}
                      defaultValue={state.business.aboutYourBusiness?.limitedCompany?.timeInBusinessYears}
                      error={aboutYourBusinessForm.formState.errors.timeInBusinessYears}
                      onChange={value => handleCustomInputChange('timeInBusinessYears', value)}
                    />
                    <Select
                      label='Months'
                      hideLabel={true}
                      required={true}
                      selectOptions={monthsOptions}
                      defaultValue={state.business.aboutYourBusiness?.limitedCompany?.timeInBusinessMonths}
                      error={aboutYourBusinessForm.formState.errors.timeInBusinessMonths}
                      onChange={value => handleCustomInputChange('timeInBusinessMonths', value)}
                    />
                  </div>
                  <Question
                    title='Did you make a net profit past year? *'
                    name='haveNetProfitLastYearLimitedCompany'
                    style='field'
                    options={[
                      { id: 'profitPastYearYes', title: 'Yes', value: true },
                      { id: 'profitPastYearNo', title: 'No', value: false }
                    ]}
                    defaultValue={state.business.aboutYourBusiness.limitedCompany?.haveNetProfitLastYear ? true : false}
                    error={aboutYourBusinessForm.formState.errors.haveNetProfitLastYear}
                    onChange={value => handleCustomInputChange('haveNetProfitLastYear', value)}
                  />
                </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <Input
                  label='Company phone number'
                  inputId='limitedCompany.phone'
                  placeholder='Company phone number'
                  required={true}
                  defaultValue={state.business.aboutYourBusiness?.limitedCompany?.phone}
                  error={aboutYourBusinessForm.formState.errors?.phone}
                  {...(isLimitedCompany ? aboutYourBusinessForm.register('phone', {
                    required: 'Company phone number is required',
                    minLength: {
                      value: 8,
                      message: 'Phone number must be at least 8 digits'
                    },
                    pattern: {
                      value: mobileNumberRegex,
                      message: 'Invalid mobile number format'
                    }
                  }) : {})}
                />
                <Input
                  label='Business email address'
                  inputId='limitedCompany.email'
                  placeholder='Business email address'
                  required={true}
                  type='email'
                  defaultValue={state.business.aboutYourBusiness?.limitedCompany?.email}
                  error={aboutYourBusinessForm.formState.errors?.email}
                  {...(isLimitedCompany ? aboutYourBusinessForm.register('email', {
                    required: 'Email address is required',
                    pattern: {
                      value: emailRegex,
                      message: 'Invalid email address format'
                    }
                  }) : {})}
                />
              </div>
            </Box>
          </>
        }

        {isLimitedCompany &&
          <>
            <Heading as='h2'>Company address details</Heading>
            <Box>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <AddressFinderInput
                  label='Company address'
                  placeholder='Company address'
                  inputId='physicalAddress'
                  required={true}
                  defaultValue={state.business.aboutYourBusiness?.limitedCompany?.physicalAddress || updatedPhysicalAddress}
                  onChange={(value, address) => handleCustomAddressFinderInputChange('physicalAddress', value, address)}
                  error={aboutYourBusinessForm.formState.errors.physicalAddress}
                />

                <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                  <div className='md:col-span-2'>
                    <Input
                      label='City'
                      inputId='physicalCity'
                      placeholder='City'
                      required={true}
                      defaultValue={state.business.aboutYourBusiness?.limitedCompany?.physicalCity}
                      error={aboutYourBusinessForm.formState.errors?.physicalCity}
                      {...aboutYourBusinessForm.register('physicalCity', { required: 'City is required' })}
                    />
                  </div>
                  <Input
                    label='Postal code'
                    inputId='physicalZIP'
                    placeholder='Code'
                    required={true}
                    defaultValue={state.business.aboutYourBusiness?.limitedCompany?.physicalZIP}
                    error={aboutYourBusinessForm.formState.errors.physicalZIP}
                    {...aboutYourBusinessForm.register('physicalZIP', { required: 'Postal code is required' })}
                  />
                </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <Select
                  label='Country'
                  required={true}
                  selectOptions={listOfCountries}
                  error={aboutYourBusinessForm.formState.errors.physicalCountry}
                  defaultValue={aboutYourBusinessForm.watch('physicalCountry')}
                  onChange={(value) => handleCustomInputChange('physicalCountry', value)}
                />
                <fieldset>
                  <legend className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Re-use information</legend>
                  <div className='flex max-sm:flex-col gap-6 max-sm:gap-1'>
                    <Checkbox
                      label='Postal address'
                      inputId='reuseCompanyAddressForPostalAddress'
                      defaultChecked={state.business.aboutYourBusiness?.limitedCompany?.reuseForPostalAddress}
                      {...aboutYourBusinessForm.register('reuseForPostalAddress')}
                    />
                    <Checkbox
                      label='Registered address'
                      inputId='reuseCompanyAddressForRegisteredAddress'
                      defaultChecked={state.business.aboutYourBusiness?.limitedCompany?.reuseForRegisteredAddress}
                      {...aboutYourBusinessForm.register('reuseForRegisteredAddress')}
                    />
                  </div>
                </fieldset>
              </div>
            </Box>
            {showYourPostalAddress &&
              <>
                <Heading as='h2'>Postal address details</Heading>
                <Box>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                    <AddressFinderInput
                      label='Company postal address'
                      placeholder='Company postal address'
                      inputId='postalAddress'
                      required={true}
                      defaultValue={state.business.aboutYourBusiness?.limitedCompany?.postalAddress || updatedPostalAddress}
                      onChange={(value, address) => handleCustomAddressFinderInputChange('postalAddress', value, address)}
                      error={aboutYourBusinessForm.formState.errors.postalAddress}
                    />

                    <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                      <div className='md:col-span-2'>
                        <Input
                          label='City'
                          inputId='postalCity'
                          placeholder='City'
                          required={true}
                          defaultValue={state.business.aboutYourBusiness?.limitedCompany?.postalCity}
                          error={aboutYourBusinessForm.formState.errors.postalCity}
                          {...aboutYourBusinessForm.register('postalCity', { required: 'City is required' })}
                        />
                      </div>
                      <Input
                        label='Postal code'
                        inputId='postalPostalCode'
                        placeholder='Code'
                        required={true}
                        defaultValue={state.business.aboutYourBusiness?.limitedCompany?.postalZIP}
                        error={aboutYourBusinessForm.formState.errors.postalZIP}
                        {...aboutYourBusinessForm.register('postalZIP', { required: 'Postal code is required' })}
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                    <Select
                      label='Country'
                      required={true}
                      selectOptions={listOfCountries}
                      error={aboutYourBusinessForm.formState.errors.postalCountry}
                      defaultValue={aboutYourBusinessForm.watch('postalCountry')}
                      onChange={(value) => handleCustomInputChange('postalCountry', value)}
                    />
                  </div>
                </Box>
              </>
            }

            {showYourRegisteredAddress &&
              <>
                <Heading as='h2'>Registered address details</Heading>
                <Box>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                    <AddressFinderInput
                      label='Company registered address'
                      placeholder='Company registered address'
                      inputId='registeredAddress'
                      required={true}
                      defaultValue={state.business.aboutYourBusiness?.limitedCompany?.registeredAddress || updatedRegisteredAddress}
                      onChange={(value, address) => handleCustomAddressFinderInputChange('registeredAddress', value, address)}
                      error={aboutYourBusinessForm.formState.errors.registeredAddress}
                    />

                    <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                      <div className='md:col-span-2'>
                        <Input
                          label='City'
                          inputId='registeredCity'
                          placeholder='City'
                          required={true}
                          defaultValue={state.business.aboutYourBusiness?.limitedCompany?.registeredCity}
                          error={aboutYourBusinessForm.formState.errors.registeredCity}
                          {...aboutYourBusinessForm.register('registeredCity', { required: 'City is required' })}
                        />
                      </div>
                      <Input
                        label='Postal code'
                        inputId='registeredPostalCode'
                        placeholder='Code'
                        required={true}
                        defaultValue={state.business.aboutYourBusiness?.limitedCompany?.registeredZIP}
                        error={aboutYourBusinessForm.formState.errors.registeredZIP}
                        {...aboutYourBusinessForm.register('registeredZIP', { required: 'Postal code is required' })}
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                    <Select
                      label='Country'
                      required={true}
                      selectOptions={listOfCountries}
                      defaultValue={aboutYourBusinessForm.watch('registeredCountry')}
                      error={aboutYourBusinessForm.formState.errors?.registeredCountry}
                      onChange={(value) => handleCustomInputChange('registeredCountry', value)}
                    />
                  </div>
                </Box>
              </>
            }
          </>
        }

        {state.isPostCall && (
          <p className="text-center text-gray-600 mt-6 max-w-2xl mx-auto">By clicking "Next" you are confirming that the above information is correct</p>
        )}

        <div className='flex justify-center py-8'>
          <Button type='submit'>Next</Button>
        </div>
      </form>
    </>
  )
}

export default AboutYourBusiness
